import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Chip
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { APIEndpointOut, DeveloperPermissionOut } from 'src/api/Api';
import { formatDate } from 'src/util/date-utils';

import ConfirmationDialog from 'src/view/components/ConfirmationDialog';

interface DeveloperPermissionManagerProps {
    userId: string;
}

const DeveloperPermissionManager: React.FC<DeveloperPermissionManagerProps> = ({ userId }) => {
    const { showSnackbar, snackbarType } = useSnackbar();
    const [permissions, setPermissions] = useState<DeveloperPermissionOut[]>([]);
    const [availableEndpoints, setAvailableEndpoints] = useState<APIEndpointOut[]>([]);
    const [loading, setLoading] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedEndpoints, setSelectedEndpoints] = useState<string[]>([]);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Confirmation Dialog State
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [permissionToRevoke, setPermissionToRevoke] = useState<string | null>(null);

    const fetchData = React.useCallback(async () => {
        setLoading(true);
        try {
            const [permResponse, endpointsResponse] = await Promise.all([
                clientService.api.listDeveloperPermissionsApiV1DevelopersDeveloperIdPermissionsGet(userId),
                clientService.api.listApiEndpointsApiV1ApiEndpointsGet({ per_page: 100 }) // Fetch all for now
            ]);

            if (permResponse.ok) {
                setPermissions(permResponse.data.permissions);
            }
            if (endpointsResponse.ok) {
                setAvailableEndpoints(endpointsResponse.data.endpoints);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            showSnackbar({ message: 'Failed to fetch permissions data', type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [userId, showSnackbar, snackbarType]); 

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleAssignOpen = () => {
        setSelectedEndpoints([]);
        setAssignDialogOpen(true);
    };

    const handleAssignSubmit = async () => {
        if (selectedEndpoints.length === 0) return;
        setActionLoading(true);
        try {
            const response = await clientService.api.bulkAssignEndpointsToDeveloperApiV1DevelopersDeveloperIdPermissionsBulkPost(userId, {
                endpoint_ids: selectedEndpoints,
                status: 'active'
            });

            if (response.ok) {
                showSnackbar({ message: 'Permissions assigned successfully', type: snackbarType.SUCCESS });
                fetchData();
                setAssignDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to assign permissions:', error);
            showSnackbar({ message: 'Failed to assign permissions', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevokeClick = (permissionId: string) => {
        setPermissionToRevoke(permissionId);
        setConfirmDialogOpen(true);
    };

    const handleRevokeConfirm = async () => {
        if (!permissionToRevoke) return;
        
        try {
            const response = await clientService.api.revokePermissionApiV1PermissionsPermissionIdDelete(permissionToRevoke);
            if (response.ok) {
                showSnackbar({ message: 'Permission revoked successfully', type: snackbarType.SUCCESS });
                fetchData();
            }
        } catch (error) {
            console.error('Failed to revoke permission:', error);
            showSnackbar({ message: 'Failed to revoke permission', type: snackbarType.ERROR });
        } finally {
            setConfirmDialogOpen(false);
            setPermissionToRevoke(null);
        }
    };

    const handleToggleSelect = (endpointId: string) => {
        setSelectedEndpoints(prev => 
            prev.includes(endpointId) 
                ? prev.filter(id => id !== endpointId)
                : [...prev, endpointId]
        );
    };

    // Filter out already assigned endpoints
    const assignedEndpointIds = new Set(permissions.map(p => p.endpoint_id));
    const unassignedEndpoints = availableEndpoints.filter(ep => !assignedEndpointIds.has(ep.id));

    return (
        <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Developer Permissions</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={handleAssignOpen}
                >
                    Assign Endpoints
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Path</TableCell>
                            <TableCell>Assigned At</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : permissions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                    No permissions assigned
                                </TableCell>
                            </TableRow>
                        ) : (
                            permissions.map((perm) => {
                                // Find endpoint details
                                const endpoint = availableEndpoints.find(ep => ep.id === perm.endpoint_id);
                                return (
                                    <TableRow key={perm.id}>
                                        <TableCell>{endpoint?.service_name || 'Unknown'}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={endpoint?.method || '???'} 
                                                size="small" 
                                                color={
                                                    endpoint?.method === 'GET' ? 'info' :
                                                    endpoint?.method === 'POST' ? 'success' :
                                                    endpoint?.method === 'PUT' ? 'warning' :
                                                    endpoint?.method === 'DELETE' ? 'error' : 'default'
                                                }
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{endpoint?.path || perm.endpoint_id}</TableCell>
                                        <TableCell>{formatDate(perm.granted_at)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={() => handleRevokeClick(perm.id)} color="error" size="small">
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Assign API Endpoints</DialogTitle>
                <DialogContent dividers>
                    {unassignedEndpoints.length === 0 ? (
                        <Typography align="center" sx={{ py: 2 }}>All available endpoints are already assigned.</Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {unassignedEndpoints.map(ep => (
                                <FormControlLabel
                                    key={ep.id}
                                    control={
                                        <Checkbox 
                                            checked={selectedEndpoints.includes(ep.id)}
                                            onChange={() => handleToggleSelect(ep.id)}
                                        />
                                    }
                                    label={
                                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip label={ep.method} size="small" variant="outlined" />
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{ep.path}</Typography>
                                            <Typography variant="caption" color="text.secondary">({ep.service_name})</Typography>
                                        </Box>
                                    }
                                />
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleAssignSubmit} 
                        variant="contained" 
                        disabled={actionLoading || selectedEndpoints.length === 0}
                    >
                        {actionLoading ? <CircularProgress size={24} /> : 'Assign Selected'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmationDialog
                open={confirmDialogOpen}
                onCancel={() => setConfirmDialogOpen(false)}
                onConfirm={handleRevokeConfirm}
                title="Revoke Permission"
                content="Are you sure you want to revoke this permission? The developer will no longer be able to access this endpoint."
            />
        </Box>
    );
};

export default DeveloperPermissionManager;
